/* eslint-disable @typescript-eslint/naming-convention */
import {
    Component,
    Injector,
    OnDestroy,
    AfterViewInit,
    AfterContentInit,
    QueryList,
    Renderer2,
    ViewChildren,
    Input,
    ViewChild,
    ChangeDetectorRef,
    ElementRef,
} from '@angular/core';

import { combineLatest, filter } from 'rxjs';

import { PrivateDataModel } from '../../Discover/Targeting/models/private-data.model';

import { EntityType } from '../../api/targeting/enum/entity.type.enum';
import { HandleTypeEnum } from '../../api/targeting/enum/handle-type.enum';
import { ScrollPanelComponent } from '../Basic/components/ScrollPanel/scroll-panel.component';
import { ButtonTypesEnum } from '../Forms/enums/button-types.enum';
import { RightSidebarComponent } from '../Layout/components/RightSidebar/right-sidebar.component';
import { TabComponent } from '../Layout/components/Tab/tab.component';
import { DiscoverRoutingService } from '../../Discover/DiscoverRouter/services/discover-routing.service';
import { ContactModel } from '../TargetingShared/model/contact.model';
import { HandleModel } from '../../Discover/Targeting/models/handle.model';
import { OutletModel } from '../../Discover/Targeting/models/outlet.model';
import { TargetingRhpDialogService } from '../TargetingShared/services/targeting-rhp-dialog.service';
import { OutletContactTabType, TargetingRhpContextsService } from '../TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingSearchService } from '../../Discover/Targeting/services/targeting-search.service';
import { TabsOrientationsEnum } from '../Layout/components/Tabs/tabs.component';
import { EntityDetailsTabComponent } from '../TargetingShared/components/EntityDetailsTab/entity-details-tab.component';
import { LivenessEnum } from '../../api/targeting/enum/liveness.enum';
import { AffiliatesTabComponent } from './components/Affiliates/affiliates-tab.component';
import { StaffSearchTabComponent } from './components/StaffSearch/staff-search-tab.component';
import { InteractionViewTabComponent } from './components/InteractionViewTab/interaction-view-tab.component';
import { RecentCoverageTabComponent } from './components/RecentCoverageTab/recent-coverage-tab.component';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../Core/Users/enums/user-permissions.enum';

interface NotesTabIndex {contactsLength: number; outletLength: number}


@Component({
    selector: 'agpr-rhp-contexts',
    templateUrl: './rhp-contexts.component.html',
    styleUrls: [
        './rhp-contexts.component.scss',
        '../Layout/components/RightSidebar/right-sidebar.component.scss'
    ],
    providers: [{
        provide: RightSidebarComponent,
        useExisting: RhpContextsComponent
    }],
    standalone: false
})
export class RhpContextsComponent extends RightSidebarComponent implements OnDestroy, AfterViewInit, AfterContentInit {

    @Input() id: string;
    @Input() set hideOutletContactTabs(isHidden: boolean) {
        this._hideOutletContactTabs = isHidden;

        if (isHidden) {
            this.visibleOutletContactTabs = [];
            this.targetingRhpContextsService.selectTab(0);
        } else {
            this.initialLoad = true;
            this.targetingRhpContextsService.selectTab(null);
        }
    }
    @Input() hiddenNoContentMessage: boolean = false;
    @Input() disableContentToggle: boolean = false;
    @Input() showFooter: boolean = true;
    @Input() showAiSearchSummary: boolean = false;
    @Input() fullHeightTakeover: boolean = false;
    @Input() hideBusinessCard: boolean = false;


    // these 2 Inputs should be used together, it's to disable the RHP tabs if no contacts are selected
    @Input() disableIfNoContactSelected: boolean = false;
    @Input() set contactSelected(contactSelected: boolean) {
        this._contactSelected = contactSelected;
        if (this._outletContactTabs) {
            this._outletContactTabs.map(tab => tab.disabled = false);
        }
    }
    _contactSelected: boolean = true;

    @ViewChild('scrollPanel') declare scrollPanel: ScrollPanelComponent;

    readonly buttonTypes = ButtonTypesEnum;
    readonly outletContactTabTypesEnum = OutletContactTabType;
    readonly outletContactTabKeys = Object.keys(OutletContactTabType);
    readonly entityTypes = EntityType;
    readonly outletLiveness = LivenessEnum;

    contact: ContactModel;
    outlet: OutletModel;
    contactPrivateData: PrivateDataModel;
    outletPrivateData: PrivateDataModel;
    entityType: EntityType;
    currentTab: OutletContactTabType;
    _hideOutletContactTabs: boolean = false;
    tabTitle: string = '';
    notesLength: number = 0;

    entityLoading: boolean = false;


    initialLoad = true;
    outletContactTabsVisible = false;
    loadingSearch = false;
    combinedTabIndex: number;
    orientation = TabsOrientationsEnum.VERTICAL;

    twitterHandle: HandleModel;

    public _outletContactTabs: QueryList<TabComponent>;
    visibleOutletContactTabs: TabComponent[];

    contactUserNotesLength: number = 0;
    outletUserNotesLength: number = 0;
    isRecentCoverage: boolean;
    hasMonitoringPermissions: boolean;

    @ViewChildren(TabComponent) set outletContactTabs(outletContactTabs: QueryList<TabComponent>) {
        // Uses a setter because the view both sets this and uses it, causing a diff checking warning
        requestAnimationFrame(() => {
            this._outletContactTabs = outletContactTabs;

            // These two variables are for keyboard accessibility only
            this.visibleOutletContactTabs = this._outletContactTabs.filter(tab => !tab.hidden);
            this.visibleTabs = this.tabs.filter(tab => !tab.hidden);

            this.visibleOutletContactTabs.forEach(tab => {
                this.removeRhpFocus(tab);
            });

            this.visibleTabs.forEach(tab => {
                this.removeRhpFocus(tab);
            });

            this.addSpacer();
            const currentCombinedTab = this.getCombinedTabByIndex(this.combinedTabIndex);
            if (currentCombinedTab && !currentCombinedTab.disabled) {
                this.addFocus(currentCombinedTab, false);
            }

            if (this.initialLoad) {
                this.resetCurrentTab();
                this.selectOutletContactTab();
                this.initialLoad = false;
            }

            // used in RR dialog, we want to disable the tabs until a contact is selected
            if (this._outletContactTabs && this.disableIfNoContactSelected) {
                this._outletContactTabs.map(tab => {
                    tab.disabled = !tab.selected && !this._contactSelected;
                });
            }
        });
        this.cdr.detectChanges();
    };

    @ViewChild('contactProfile') contactProfileTab: EntityDetailsTabComponent;
    @ViewChild('outletProfile') outletProfileTab: EntityDetailsTabComponent;
    @ViewChild('affiliatesTab') affiliatesTab: AffiliatesTabComponent;
    @ViewChild('staffSearchTab') staffSearchTab: StaffSearchTabComponent;
    @ViewChild('interactionViewTab') interactionViewTab: InteractionViewTabComponent;
    @ViewChild('recentCoverageTab') recentCoverageTab: RecentCoverageTabComponent;

    constructor(
        protected renderer: Renderer2,
        private discoverRoutingService: DiscoverRoutingService,
        private currentUserStateService: CurrentUserStateService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public injector: Injector,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public targetingSearchService: TargetingSearchService,
        private cdr: ChangeDetectorRef,
        protected el: ElementRef
    ) {
        super(renderer, el);
        this.subscriptions.push(
            this.targetingSearchService.getSearchLoading().subscribe(loading => this.loadingSearch = loading),
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (this._outletContactTabs?.length) {
                        this.resetCurrentTab();
                        this.selectOutletContactTab();
                    }
                }
            ),
            this.targetingRhpContextsService.getCurrentEntityType().pipe(
                filter(x => !!x)
            ).subscribe(type => {
                this.entityType = type;
            }),
            this.targetingRhpContextsService.getOutlet().subscribe(x =>  {
                this.outlet = x.outlet;
                if (x.entityType === EntityType.OUTLET && this.outlet) {
                    this.twitterHandle = this.outlet.handles.find(handle => handle.handleType === HandleTypeEnum.TWITTER);
                }
                this.resetScrollPanel();
                if (x.outlet === null) {
                    this.outletUserNotesLength = 0;
                    this.setNotesLength();
                }
                this.selectOutletContactTab();
            }),
            combineLatest([
                this.targetingRhpContextsService.getContact(),
                this.targetingRhpContextsService.getContactPrivateData(),
                this.targetingRhpContextsService.getOutletPrivateData()
            ]).subscribe(([contact, privateDataContact, privateDataOutlet]) => {
                this.contact = contact;
                if (this.contact) {
                    this.twitterHandle = this.contact.handles.find(handle => handle.handleType === HandleTypeEnum.TWITTER);
                }
                this.resetScrollPanel();
                this.selectOutletContactTab();

                this.contactPrivateData = privateDataContact;
                this.contactUserNotesLength = 0;
                if (this.contact?.privateContactMetadata) {
                    this.contactUserNotesLength = this.contact.userNotes.length ?? 0;
                } else if (this.contactPrivateData) {
                    this.contactUserNotesLength = this.contactPrivateData.notes?.length ?? 0;
                }

                this.outletPrivateData = privateDataOutlet;
                this.outletUserNotesLength = privateDataOutlet?.notes?.length?? 0;

                this.setNotesLength();
            }),

            this.targetingRhpContextsService.getCurrentOutletContactTab().subscribe(tab => {
                const switchNeeded = this.currentTab !== tab;
                this.currentTab = tab;
                if (this._outletContactTabs && switchNeeded) {
                    this.selectOutletContactTab();
                }
            }),
            this.targetingRhpContextsService.currentTab$.subscribe(tabIndex => {
                this.selectTabByIndex(tabIndex, true, false);
            }),
            this.targetingRhpContextsService.getOutletContactTabsVisible().subscribe(x => this.outletContactTabsVisible = x),
            this.currentUserStateService.getState().subscribe(userState => {
                this.hasMonitoringPermissions = userState.permissions.includes(userPermissionsEnum.advancedMonitoring);
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    getIconClasses(tab: any): string {
        const base = 'agpr-rightSidebar-tab-icon ';
        const baseClass = base + tab.icon;
        const iconClassMap: { [key: string]: string } = {
            'fa-kit-duotone fa-agpr-outlet': 'fa-agpr-outlet-solid fa-kit',
            'fa-kit-duotone fa-agpr-lists': 'fa-agpr-lists-solid fa-kit',
            'fa-kit-duotone fa-agpr-engage': 'fa-kit fa-agpr-engage-solid',
            'fa-kit-duotone fa-agpr-insights': 'fa-kit fa-agpr-insights-solid',
            'fa-kit-duotone fa-agpr-notes': 'fa-kit fa-agpr-notes-solid',
        };

        if (tab.icon.includes('agpr')) {
            if (iconClassMap[tab.icon]) {
                return tab.selected ? base + ' ' + iconClassMap[tab.icon] : baseClass;
            } else {
                return baseClass;
            }
        } else {
            const iconClass = tab.selected ? 'fa-solid' : 'fa-regular fa-duotone';
            return baseClass + ' ' + iconClass;
        }
    }

    setNotesLength() {
        if(this.entityType === this.entityTypes.CONTACT){
            this.notesLength = this.contactUserNotesLength + this.outletUserNotesLength;
        } else {
            this.notesLength = this.outletUserNotesLength;
        }
    }

    updateNotesLength(notes: NotesTabIndex) {
        this.contactUserNotesLength = notes.contactsLength;
        this.outletUserNotesLength = notes.outletLength;
        this.setNotesLength();
    }

    private resetCurrentTab() {
        if (this.showAiSearchSummary) {
            const recommendedSearchSummaryTab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.recommendedSearchSummary'));
            if (recommendedSearchSummaryTab && !recommendedSearchSummaryTab.hidden) {
                this.currentTab = OutletContactTabType.recommendedSearch;
            } else {
                this.currentTab = OutletContactTabType.contactProfile;
            }
        } else if (this.entityType === EntityType.CONTACT) {
            const contactProfileTab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.contactProfile'));
            if (contactProfileTab && !contactProfileTab.hidden) {
                this.currentTab = OutletContactTabType.contactProfile;
            } else {
                this.currentTab = OutletContactTabType.outletProfile;
            }
        } else {
            const outletProfileTab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.outletProfile'));
            if (outletProfileTab && !outletProfileTab.hidden) {
                this.currentTab = OutletContactTabType.outletProfile;
            } else {
                this.currentTab = OutletContactTabType.staffSearch;
            }
        }
    }

    selectOutletContactTab() {
        if (!this._outletContactTabs || !this._outletContactTabs.length) {
            return;
        }
        let tab;
        switch (this.currentTab) {
            case OutletContactTabType.contactProfile:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.contactProfile'));
                break;
            case OutletContactTabType.outletProfile:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.outletProfile'));
                break;
            case OutletContactTabType.socialMedia:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.socialMedia'));
                break;
            case OutletContactTabType.staffSearch:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.staffSearch'));
                break;
            case OutletContactTabType.affiliates:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.affiliates'));
                break;
            case OutletContactTabType.listMembership:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.listMembership'));
                break;
            case OutletContactTabType.recentCoverage:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.recentCoverage'));
                break;
            case OutletContactTabType.interactionView:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.interactionView'));
                break;
            case OutletContactTabType.recommendedSearch:
                tab = this._outletContactTabs.toArray().find(x => x.id?.endsWith('targetingRhpTab.recommendedSearchSummary'));
                break;
        }
        this.selectTab(tab, false);
        this.addFocus(tab, false);
    }

    // There will be variants of this method depending which tab it comes from and which
    // data is already loaded
    // TODO : Type this event
    spawnStaffProfileTakeover(event: any) {
        const dialog = this.targetingRhpDialogService.openTakeOver({
            inputs: {
                contactId: event.contactId,
                outletId: event.outletId,
                entityType : event.entityType,
                fullHeight: this.fullHeightTakeover,
                disableContentToggle: this.disableContentToggle
            },
            outputs: {
                dialogClose: () => {
                    this.closeFocus();
                }
            }
        });
    }

    spawnOutletProfileTakeover(event: any) {
        const dialog = this.targetingRhpDialogService.openTakeOver({
            inputs: {
                outletId:  event.outletId,
                entityType : EntityType.OUTLET,
                fullHeight: this.fullHeightTakeover,
                disableContentToggle: this.disableContentToggle
            },
            outputs: {
                dialogClose: () => {
                    this.closeFocus();
                }
            }
        });
    }

    ngAfterContentInit(): void {
        this.contentInitialized = true;
        this.selectTabByIndex(this.initialTabIndex || 0, true, false);
    }

    ngAfterViewInit(): boolean {

        const tab = this.tabs.toArray()[this._selectedTabIndex];

        if (tab && this.tabHeader !== tab.header && !tab.hidden) {
            // re-select the tab if the view initialized a header
            // has to be a separate frame to make sure it works outside of dev mode
            requestAnimationFrame(() => {
                this.selectTab(tab, false);
            });
            return true;
        }

        return false;
    }

    /**
     * resets RHP scroll panel position to top
     */
    resetScrollPanel(): void {
        if (this.scrollPanel) {
            this.scrollPanel.scrollTo();
        }
    }

    removeRhpFocus(tab: TabComponent): void {
        const removeTabFocus = this.el.nativeElement.querySelector('#' + tab.id.replace(/\./g, '\\.')) as HTMLElement;
        if (removeTabFocus) {
            removeTabFocus.setAttribute('tabindex', '-1');
        }

        this.unfocusedTabs = Array.from(this.el.nativeElement.querySelectorAll('[role=tab]'));

        for (let i = 0; i < this.unfocusedTabs.length; i++) {
            const unfocusedTab = this.unfocusedTabs[i];
            unfocusedTab.setAttribute('tabindex', '-1');
        }
    }

    // Adding a spacer to separate the tabs
    addSpacer(): void {
        const firstSpacerTab = this.el.nativeElement.querySelector('.agpr-rightSidebar-tab--spacer');
        firstSpacerTab?.classList?.add('spacerTab');
    }

    selectTab(selectedTab: TabComponent, emitChanges: boolean = true): void {
        if (!selectedTab) {
            return;
        }

        let idSuffix;

        if (selectedTab.id.includes('fullProfileDialog')) {
            idSuffix = selectedTab.id.split('.')[2];
        } else {
            idSuffix = selectedTab.id.split('.')[1];
        }
        this.isRecentCoverage = false;

        // TODO : add the rest
        switch (idSuffix) {
            case 'contactProfile':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.contactProfile);
                break;
            case 'outletProfile':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.outletProfile);
                break;
            case 'staffSearch':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.staffSearch);
                break;
            case 'affiliates':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.affiliates);
                break;
            case 'listMembership':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.listMembership);
                break;
            case 'socialMedia':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.socialMedia);
                break;
            case 'recentCoverage':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.recentCoverage);
                this.isRecentCoverage = true;
                break;
            case 'interactionView':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.interactionView);
                break;
            case 'recommendedSearchSummary':
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.recommendedSearch);
                break;
        }

        if (!selectedTab) {
            this.unselectTab();
            return;
        }
        if (selectedTab.disabled) {
            return;
        }

        let index = 0;

        this.tabs.forEach((tab, i) => {
            tab.selected = tab === selectedTab;
            if (tab.selected && !tab.hidden) {
                index = i;
            }
        });

        if (this._outletContactTabs) {
            this._outletContactTabs.forEach((tab, i) => {
                tab.selected = tab === selectedTab;
                if (tab.selected) {
                    index = i;
                }
            });
        }

        this.tabFooter = selectedTab.footer;

        if (selectedTab.id.includes('_targetingRhpTab.notes')) {
            this.tabTitle = selectedTab.tabTitle;
        } else {
            this.tabTitle = selectedTab.tooltip ? selectedTab.tooltip : '';
        }

        this._selectedTabIndex = index;
        this.initialTabIndex = null;
        if (emitChanges) {
            this.selectedTabIndexChange.emit(index);
        }

        this.tabHeader = selectedTab.header;

        this.resetScrollPanel();

        /* #region keyboard accessibility code
        *   Added new variables so that this does not affect any of the other indexes or tab lists.
        *   'visibleTabs' selects all the normal tabs that are shown, and 'visibleOutletContactTabs selects all outletContactTabs that are shown.
        *   The 'combinedTabIndex' combines both 'index' and 'keyTabIndex' so that the user can switch through all tabs.
        *   If we do not set that, the tabs have equal, but separate indexes,
        *   and that only respects indexes of normal tabs and not the indexes of outletContactTabs.
        *   'isOutletTab' checks if there are any visible outlet contact tabs.
        */

        let keyTabIndex = 0;
        let isOutletTab = false;

        // For tabs in the first list

        if (this.visibleOutletContactTabs) {
            this.visibleOutletContactTabs.forEach((tab, i) => {
                tab.selected = tab === selectedTab;
                if (tab.selected) {
                    keyTabIndex = i;
                    isOutletTab = true;
                }
            });
        }

        // For tabs in the second list

        this.visibleTabs?.forEach((tab, i) => {
            tab.selected = tab === selectedTab;
            if (tab.selected) {
                keyTabIndex = i;
            }
        });

        if (isOutletTab) {
            this.combinedTabIndex = keyTabIndex;
        } else {
            this.combinedTabIndex = keyTabIndex + this.visibleOutletContactTabs?.length;
        }
    }

    // To select all tabs in Discover by combining both tab lists into one
    // The function below is only for keyboard accessibility

    selectCombinedTabsByIndex(index: number, emitChanges: boolean = true): void {
        if (!this.tabs || !this._outletContactTabs) {
            return;
        }
        if (index < this.visibleOutletContactTabs.length) {
            this.selectTab(this.visibleOutletContactTabs[index] || this._outletContactTabs.first, emitChanges);
            this.addFocus(this.visibleOutletContactTabs[index] || this._outletContactTabs.first);
        } else {
            if (this.visibleTabs.length > 0) {
                this.selectTab(this.visibleTabs[index - this.visibleOutletContactTabs.length], emitChanges);
                this.addFocus(this.visibleTabs[index - this.visibleOutletContactTabs.length]);
            } else {
                this.selectTab(this.visibleOutletContactTabs[0], emitChanges);
                this.addFocus(this.visibleOutletContactTabs[0]);
            }
        }
    }

    /**
    * This function is only used for keyboard accessibility
    * it is called whenever the user presses the arrow keys, enter & space
    */

    onKeyUp(event): void {
        if (!this.isKeyOrientationMatching(event)) {
            return;
        }
        this.removeFocus();
        let nonDisabledTabFound: boolean = false;
        switch (event.key) {
            case this.actionKeysEnum.RIGHT:
            case this.actionKeysEnum.DOWN:
                while (!nonDisabledTabFound) {
                    this.combinedTabIndex = this.combinedTabIndex + 1 === this.visibleOutletContactTabs.length + this.visibleTabs.length ?
                        0 : this.combinedTabIndex + 1;
                    if (!(this.getCombinedTabByIndex(this.combinedTabIndex)?.disabled)) {
                        nonDisabledTabFound = true;
                    }
                }
                break;
            case this.actionKeysEnum.LEFT:
            case this.actionKeysEnum.UP:
                while (!nonDisabledTabFound) {
                    this.combinedTabIndex = this.combinedTabIndex - 1 < 0 ?
                        this.visibleOutletContactTabs.length + this.visibleTabs.length - 1 : this.combinedTabIndex - 1;
                    if (!(this.getCombinedTabByIndex(this.combinedTabIndex)?.disabled)) {
                        nonDisabledTabFound = true;
                    }
                }
                break;
        }
        this.selectCombinedTabsByIndex(this.combinedTabIndex);
    }

    private getCombinedTabByIndex(combinedIndex: number): TabComponent {
        return combinedIndex < this.visibleOutletContactTabs.length ?
            this.visibleOutletContactTabs[combinedIndex] :
            this.visibleTabs[combinedIndex - this.visibleOutletContactTabs.length];
    }

    /**
     * Handles the scroll event for the current tab.
     *
     * Depending on which tab is selected (Affiliates, Staff Search, Interaction View,
     * or Recent Coverage), this method forwards the scroll position (scrollTop)
     * to the corresponding tab component.
     */
    onScroll(scrollTop: number) {
        if (this.currentTab === OutletContactTabType.affiliates) {
            this.affiliatesTab.handleScroll(scrollTop);
        } else if (this.currentTab === OutletContactTabType.staffSearch) {
            this.staffSearchTab.handleScroll(scrollTop);
        } else if (this.currentTab === OutletContactTabType.interactionView) {
            this.interactionViewTab.handleScroll(scrollTop);
        } else if (this.currentTab === OutletContactTabType.recentCoverage) {
            this.recentCoverageTab.handleScroll(scrollTop);
        }
    }

    // #endregion keyboard accessibility code
}
